import { useSearchState } from '../use-search-state';

import { useGetJobPreferencesIndustries } from './use-get-job-preferences-industries';

export type FilledPreferences = {
  jobTitle?: string;
  location?: {
    cityId: string;
    text: string;
    radius?: number;
  };
  disciplines?: string[];
  employmentTypes?: { fullTime: boolean; partTime: boolean };
  industries?: string[];
  maxCareerLevel?: number;
  minCareerLevel?: number;
  openToOtherIndustries?: boolean;
  remoteOption?: {
    office: boolean;
    partlyHome: boolean;
    mostlyHome: boolean;
    homeOffice: boolean;
  };
  salary?: {
    min: number | null;
  };
};

export const useSearchStateFilledJobPreferences = (): {
  filledPreferences: FilledPreferences;
  filledPreferencesTrackingContext: Array<string>;
  filledPreferencesTrackingElementDetail: Array<string>;
} => {
  const { searchQuery } = useSearchState();
  const { data, isLoading, error } = useGetJobPreferencesIndustries();

  const filledPreferences: FilledPreferences = {};
  const filledPreferencesTrackingContext = [];
  const filledPreferencesTrackingElementDetail = [];

  // Job Title
  if (searchQuery.keywords) {
    filledPreferences.jobTitle = searchQuery.keywords;
    filledPreferencesTrackingContext.push('job_title_updated');
    filledPreferencesTrackingElementDetail.push('ideal_positions');
  }

  // Location and Radius
  if (searchQuery.location?.text && searchQuery.location?.cityId) {
    filledPreferences.location = {
      cityId: searchQuery.location.cityId,
      text: searchQuery.location.text,
    };

    if (searchQuery.location.radius) {
      const distanceSliderMarks = [5, 20, 50, 100];
      const isValidRadius =
        distanceSliderMarks.includes(searchQuery.location.radius) ||
        searchQuery.location.radius > 100;

      // BE uses 0 for >100
      if (isValidRadius) {
        filledPreferences.location.radius =
          searchQuery.location.radius > 100 ? 0 : searchQuery.location.radius;

        filledPreferencesTrackingContext.push('radius_updated');
      }
    }

    filledPreferencesTrackingContext.push('city_updated');
    filledPreferencesTrackingElementDetail.push('preferred_locations');
  }

  // Career Level
  if (searchQuery.filter?.careerLevel?.id) {
    const numericIds = searchQuery.filter.careerLevel.id
      .filter((id: string | null): id is string => id !== null)
      .map((id) => parseInt(id.split('.')[0], 10));

    if (numericIds.length > 0) {
      const minCareerLevel = Math.min(...numericIds);
      // for when only one careerLevel is selected in the filters, to not have
      // the min and the max with the same values
      const maxCareerLevel =
        numericIds.length > 1 ? Math.max(...numericIds) : undefined;

      filledPreferences.minCareerLevel = minCareerLevel;
      filledPreferences.maxCareerLevel = maxCareerLevel;
    }

    filledPreferencesTrackingContext.push('career_level_updated');
    filledPreferencesTrackingElementDetail.push('career_level');
  }

  // Discipline
  if (searchQuery.filter?.discipline?.id) {
    filledPreferences.disciplines = searchQuery.filter.discipline.id.filter(
      (id: string | null): id is string => id !== null
    );
    filledPreferencesTrackingContext.push('discipline_updated');
    filledPreferencesTrackingElementDetail.push('preferred_discipline');
  }

  // Employment Type
  if (searchQuery.filter?.employmentType?.id) {
    const employmentTypes = searchQuery.filter.employmentType.id
      .filter((id: string | null): id is string => id !== null)
      .map((id) => id.split('.')[0]);

    filledPreferences.employmentTypes = {
      fullTime: employmentTypes.includes('FULL_TIME'),
      partTime: employmentTypes.includes('PART_TIME'),
    };

    filledPreferencesTrackingContext.push('working_hours_updated');
    filledPreferencesTrackingElementDetail.push('working_hours');
  }

  // Industry
  if (
    searchQuery.filter?.industry?.id &&
    data?.industries &&
    !isLoading &&
    !error
  ) {
    // job-preferences only accepts main industries (e.g.: 190000)
    // so we filter out sub-industries (e.g.: 190500)
    const mainIndustriesIds =
      data.industries.map((industry) => industry?.id || '') || [];
    const industries = searchQuery.filter.industry.id.filter(
      (id: string | null): id is string => id !== null
    );
    const filteredIndustries = industries.filter((industry) =>
      mainIndustriesIds.includes(industry)
    );
    filledPreferences.industries = filteredIndustries.slice(0, 3);
    filledPreferences.openToOtherIndustries = filteredIndustries.length > 3;
    filledPreferencesTrackingContext.push('industry_updated');
    filledPreferencesTrackingElementDetail.push('preferred_industry');
  }

  // Remote Option
  if (searchQuery.filter?.remoteOption?.id) {
    const remoteOptions = searchQuery.filter.remoteOption.id
      .filter((id: string | null): id is string => id !== null)
      .map((id) => id.split('.')[0]);

    filledPreferences.remoteOption = {
      office: remoteOptions.includes('NON_REMOTE'),
      partlyHome: remoteOptions.includes('PARTLY_REMOTE'),
      mostlyHome: remoteOptions.includes('PARTLY_REMOTE'),
      homeOffice: remoteOptions.includes('FULL_REMOTE'),
    };

    filledPreferencesTrackingContext.push('home_office_preference_updated');
    filledPreferencesTrackingElementDetail.push('home_office');
  }

  // Salary
  if (
    searchQuery.filter?.salary?.min !== undefined &&
    searchQuery.filter?.salary?.min !== 0
  ) {
    filledPreferences.salary = {
      min: searchQuery.filter.salary.min,
    };
    filledPreferencesTrackingContext.push('salary_expectations_updated');
    filledPreferencesTrackingElementDetail.push('salary_expectations');
  }

  return {
    filledPreferences,
    filledPreferencesTrackingContext,
    filledPreferencesTrackingElementDetail,
  };
};
